/**
 * EventTable Component
 * Reusable table for displaying evidence events with metadata details
 */

import React, { useState } from 'react';
import { Tag, Tooltip, Button, Descriptions, Modal, Space, Typography, Divider, Card, Alert } from 'antd';
import { EyeOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { TGDataTable } from '../../../components/common';
import { colors } from '../../../theme/designTokens';

const { Text } = Typography;

/**
 * Event Detail Modal with Navigation
 */
function EventDetailModal({ visible, events, selectedIndex, onClose, onNavigate }) {
  if (!events || events.length === 0 || selectedIndex < 0 || selectedIndex >= events.length) {
    return null;
  }

  const event = events[selectedIndex];

  const footer = [
    <Button
      key="prev"
      disabled={selectedIndex === 0}
      onClick={() => onNavigate('prev')}
      icon={<LeftOutlined />}
    >
      Previous
    </Button>,
    <Button
      key="next"
      disabled={selectedIndex === events.length - 1}
      onClick={() => onNavigate('next')}
      icon={<RightOutlined />}
    >
      Next
    </Button>,
    <Button key="close" type="primary" onClick={onClose}>
      Close
    </Button>,
  ];

  return (
    <Modal
      title={
        <Space>
          <span>Event #{event.id} Details</span>
          <Tag color={colors.primary[500]}>
            {selectedIndex + 1} of {events.length}
          </Tag>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={footer}
      width={900}
      destroyOnClose
    >
      <Descriptions bordered column={1} size="small">
        <Descriptions.Item label="Event ID">
          <span style={{ fontFamily: 'Monaco, monospace', fontWeight: 600 }}>#{event.id}</span>
        </Descriptions.Item>

        {/* Traceability: Project */}
        {event.project_name && (
          <Descriptions.Item label="Project">
            <Text strong>{event.project_name}</Text>
          </Descriptions.Item>
        )}

        {/* Traceability: Connector */}
        {event.connector && (
          <Descriptions.Item label="Connector">
            <Space>
              <Text strong>{event.connector.name}</Text>
              <Tag color="cyan">{event.connector.type}</Tag>
              <Tag color={event.connector.status === 'active' ? 'green' : 'default'}>
                {event.connector.status}
              </Tag>
            </Space>
          </Descriptions.Item>
        )}

        <Descriptions.Item label="Timestamp">
          {event.ts ? new Date(event.ts).toLocaleString() : '—'}
        </Descriptions.Item>

        <Descriptions.Item label="Event Type">{event.event_type || '—'}</Descriptions.Item>

        <Descriptions.Item label="Provider">
          <Tag>{event.provider}</Tag>
        </Descriptions.Item>

        <Descriptions.Item label="PII Detected">
          {event.pii_detected ? <Tag color="red">Yes</Tag> : <Tag color="green">No</Tag>}
        </Descriptions.Item>

        {event.pii_types && event.pii_types.length > 0 && (
          <Descriptions.Item label="PII Types">
            <Space wrap>
              {event.pii_types.map((type) => (
                <Tag key={type} color="red">
                  {type}
                </Tag>
              ))}
            </Space>
          </Descriptions.Item>
        )}

        <Descriptions.Item label="Masking Enabled">
          {event.masking_enabled ? <Tag color="green">Yes</Tag> : <Tag color="orange">No</Tag>}
        </Descriptions.Item>

        <Descriptions.Item label="Storage Tier">{event.storage_tier || '—'}</Descriptions.Item>

        {event.archived_at && (
          <Descriptions.Item label="Archived At">
            <Space>
              <Tag color="orange">Archived</Tag>
              {new Date(event.archived_at).toLocaleString()}
            </Space>
          </Descriptions.Item>
        )}

        <Descriptions.Item label="Created At">
          {event.created_at ? new Date(event.created_at).toLocaleString() : '—'}
        </Descriptions.Item>
      </Descriptions>

      {/* Control Violations */}
      {event.control_violations && event.control_violations.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <Divider style={{ margin: '16px 0' }}>
            Control Violations ({event.control_violations.length})
          </Divider>
          <Alert
            message="This event triggered the following control failures"
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
          />
          {event.control_violations.map((violation, idx) => (
            <Card
              key={idx}
              size="small"
              style={{ marginBottom: 8 }}
              title={
                <Space>
                  <Tag color={violation.status === 'fail' ? 'red' : 'orange'}>
                    {violation.control_code}
                  </Tag>
                  <Text strong>{violation.control_name}</Text>
                </Space>
              }
            >
              <Descriptions size="small" column={2}>
                {violation.control_category && (
                  <Descriptions.Item label="Category" span={2}>
                    <Tag>{violation.control_category}</Tag>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Status">
                  <Tag color={violation.status === 'fail' ? 'red' : 'orange'}>
                    {violation.status.toUpperCase()}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Severity">
                  <Tag color={violation.severity === 'high' ? 'red' : 'orange'}>
                    {violation.severity}
                  </Tag>
                </Descriptions.Item>
                {violation.rationale && (
                  <Descriptions.Item label="Rationale" span={2}>
                    {violation.rationale}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          ))}
        </div>
      )}

      {/* Event Metadata */}
      {event.event_metadata && Object.keys(event.event_metadata).length > 0 && (
        <div style={{ marginTop: 24 }}>
          <Divider style={{ margin: '16px 0' }}>Event Metadata</Divider>
          <pre
            style={{
              background: '#f5f5f5',
              padding: 12,
              borderRadius: 4,
              fontSize: 12,
              maxHeight: 300,
              overflow: 'auto',
            }}
          >
            {JSON.stringify(event.event_metadata, null, 2)}
          </pre>
        </div>
      )}
    </Modal>
  );
}

/**
 * EventTable Component
 */
function EventTable({ data, loading, fetchAll }) {
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const handleViewDetail = (record) => {
    const index = data.findIndex((e) => e.id === record.id);
    setSelectedIndex(index);
    setDetailVisible(true);
  };

  const handleNavigate = (direction) => {
    if (direction === 'prev' && selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    } else if (direction === 'next' && selectedIndex < data.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  /**
   * Transform data for export with all detail fields including metadata
   */
  const transformForExport = (events) => {
    return events.map((event) => {
      const baseData = {
        'Event ID': event.id,
        'Project': event.project_name || '',
        'Connector Name': event.connector?.name || '',
        'Connector Type': event.connector?.type || '',
        'Connector Status': event.connector?.status || '',
        'Timestamp': event.ts ? new Date(event.ts).toLocaleString() : '',
        'Event Type': event.event_type || '',
        'Provider': event.provider || '',
        'PII Detected': event.pii_detected ? 'Yes' : 'No',
        'PII Types': event.pii_types && event.pii_types.length > 0 ? event.pii_types.join(', ') : 'None',
        'Masking Enabled': event.masking_enabled ? 'Yes' : 'No',
        'Storage Tier': event.storage_tier || '',
        'Archived At': event.archived_at ? new Date(event.archived_at).toLocaleString() : '',
        'Created At': event.created_at ? new Date(event.created_at).toLocaleString() : '',
      };

      // Add control violations if present
      if (event.control_violations && event.control_violations.length > 0) {
        baseData['Control Violations Count'] = event.control_violations.length;
        baseData['Control Violations'] = event.control_violations
          .map((v) => `${v.control_code} (${v.status})`)
          .join('; ');
      }

      // Add event metadata fields as individual columns
      if (event.event_metadata && typeof event.event_metadata === 'object') {
        Object.keys(event.event_metadata).forEach((key) => {
          const value = event.event_metadata[key];
          baseData[`metadata_${key}`] = typeof value === 'object' ? JSON.stringify(value) : String(value);
        });
      }

      return baseData;
    });
  };

  // Generate dynamic filter options
  const eventTypeFilters = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    const types = [...new Set(data.map(e => e.event_type))].sort();
    return types.map(t => ({ label: t, value: t }));
  }, [data]);

  const providerFilters = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    const providers = [...new Set(data.map(e => e.provider))].sort();
    return providers.map(p => ({ label: p, value: p }));
  }, [data]);

  const piiTypesFilters = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    const allTypes = new Set();
    data.forEach(e => {
      if (e.pii_types && Array.isArray(e.pii_types)) {
        e.pii_types.forEach(type => allTypes.add(type));
      }
    });
    return [...allTypes].sort().map(type => ({ label: type, value: type }));
  }, [data]);

  const columns = [
    {
      key: 'id',
      title: 'Event ID',
      dataIndex: 'id',
      width: 100,
      fixed: 'left',
      render: (id) => <span style={{ fontFamily: 'Monaco, monospace' }}>#{id}</span>,
      sortable: true,
      filterable: true,
      filterType: 'number',
    },
    {
      key: 'ts',
      title: 'Timestamp',
      dataIndex: 'ts',
      width: 180,
      render: (ts) => {
        if (!ts) return <span style={{ color: colors.text.secondary }}>—</span>;
        const date = new Date(ts);
        return (
          <div>
            <div style={{ fontSize: 12 }}>{date.toLocaleDateString()}</div>
            <div style={{ fontSize: 11, color: colors.text.secondary }}>
              {date.toLocaleTimeString()}
            </div>
          </div>
        );
      },
      sortable: true,
    },
    {
      key: 'event_type',
      title: 'Type',
      dataIndex: 'event_type',
      width: 150,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: eventTypeFilters,
    },
    {
      key: 'provider',
      title: 'Provider',
      dataIndex: 'provider',
      width: 120,
      render: (provider) => <Tag>{provider}</Tag>,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: providerFilters,
    },
    {
      key: 'pii_types',
      title: 'PII Types',
      dataIndex: 'pii_types',
      width: 200,
      render: (types) => {
        if (!types || types.length === 0)
          return <span style={{ color: colors.text.secondary }}>None</span>;
        return (
          <Space wrap>
            {types.slice(0, 3).map((type) => (
              <Tag key={type} color="red" size="small">
                {type}
              </Tag>
            ))}
            {types.length > 3 && (
              <Tooltip title={types.slice(3).join(', ')}>
                <Tag size="small">+{types.length - 3} more</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
      sortable: true,
      filterable: true,
      filterType: 'multiselect',
      filterOptions: piiTypesFilters,
    },
    {
      key: 'event_metadata',
      title: 'Metadata',
      dataIndex: 'event_metadata',
      width: 180,
      render: (metadata) => {
        if (!metadata || Object.keys(metadata).length === 0)
          return <span style={{ color: colors.text.secondary }}>—</span>;
        const keys = Object.keys(metadata);
        return (
          <Tooltip
            title={
              <div style={{ fontSize: 11 }}>
                {keys.map((key) => (
                  <div key={key}>
                    <strong>{key}:</strong> {JSON.stringify(metadata[key])}
                  </div>
                ))}
              </div>
            }
          >
            <span style={{ fontSize: 11, cursor: 'help' }}>
              {keys.length} field{keys.length !== 1 ? 's' : ''}
            </span>
          </Tooltip>
        );
      },
      sortable: true,
      filterable: true,
      filterType: 'text',
    },
    {
      key: 'actions',
      title: 'Actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="View Details">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
            style={{ color: colors.primary[500] }}
          >
            Details
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <TGDataTable
        data={data}
        columns={columns}
        loading={loading}
        rowKey="id"
        pagination="client"
        pageSize={10}
        showExport
        showColumnConfig
        showFilters
        exportFilename="evidence_events"
        transformForExport={transformForExport}
        presetKey="trustgrid.events"
      />

      <EventDetailModal
        visible={detailVisible}
        events={data}
        selectedIndex={selectedIndex}
        onClose={() => setDetailVisible(false)}
        onNavigate={handleNavigate}
      />
    </>
  );
}

export default EventTable;
